import * as React from "react";
import { connect, Dispatch } from "react-redux";

import Setup from "../components/Setup";
import {
  handleAuthenticatedUser,
  refreshTokens,
  redirectToConsole,
} from "../actions";
import { StoreState, SetupState } from "../types";
import {
  getUserSetupState,
  getRegistrationToken,
  getIsImaUser,
} from "../reducers";

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  /**
   * Manages the setup state. The setup state is passed into this component by the
   * getUserSetupState reducer, but this prop is not passed straight through.
   * This component stores the current setupState in its internal state, and
   * updates to the latest property value only when the updateState callback is called.
   * This callback is passed to the underlying Setup component. This is so that rather
   * than jumping pages as soon as the user enters their MFA or Password reset
   * information they are given a moment to read the success message and
   * manually click the continue button. The continue button will be wired up to the
   * updateState callback triggering the UI to update to the current setup state.
   */
  class SetupContainer extends React.Component<
    SetupContainerProps,
    SetupContainerState
  > {
    constructor(props: SetupContainerProps) {
      super(props);
      this.state = { setupState: props.setupState };
    }

    componentDidUpdate(prevProps: SetupContainerProps) {
      // because of the way we are overloading the /setup route
      // we are explicitly forcing a re-render if the SetupState
      // changes. currently, the "complete" state is the final stage
      // of setup and exists only to signal we are ready to redirect
      // the user to the console app
      if (
        prevProps.setupState !== this.props.setupState &&
        this.props.setupState !== SetupState.complete
      ) {
        this.setState({ setupState: this.props.setupState });
      }
    }

    render() {
      return (
        <Setup
          setupState={this.state.setupState}
          updateState={this.updateState}
        />
      );
    }

    /**
     * Updates the SetupState...if the new state indicates
     * the user is finished setting up MFA, then we need
     * to refresh the user's tokens to update claims
     */
    updateState = (setupState: SetupState): Promise<boolean> => {
      // if we're finished with setup and we came from IMA, then we
      // need to prevent the typical redirection by overriding state
      if (setupState === SetupState.complete && this.props.isImaUser) {
        setupState = SetupState.completeImaUser;
      }

      switch (setupState) {
        case SetupState.completedPassword:
          return this.props.handleAuthenticatedUser();
        case SetupState.complete:
        case SetupState.completedMfa:
          return this.props.redirectToConsole();
        default:
          this.setState({ setupState });
          return Promise.resolve(true);
      }
    };
  }
);

export interface SetupContainerProps {
  setupState: SetupState;
  registrationToken: string | undefined;
  isImaUser: boolean | undefined;

  /** Kicks the user over to console */
  redirectToConsole(): Promise<boolean>;
  handleAuthenticatedUser(): Promise<boolean>;
}

export interface SetupContainerState {
  setupState: SetupState;
}

function mapStateToProps(state: StoreState) {
  return {
    setupState: getUserSetupState(state),
    registrationToken: getRegistrationToken(state),
    isImaUser: getIsImaUser(state),
  };
}

function mapDispatchToProps(
  dispatch: Dispatch<StoreState>
): Pick<SetupContainerProps, "handleAuthenticatedUser" | "redirectToConsole"> {
  return {
    async handleAuthenticatedUser(): Promise<boolean> {
      await dispatch(refreshTokens());
      return dispatch(handleAuthenticatedUser());
    },
    async redirectToConsole(): Promise<boolean> {
      await dispatch(refreshTokens());
      return dispatch(redirectToConsole());
    },
  };
}
